import { getImageKitUrl } from 'core-lib/utils/imageKit';
import { getLocalProfile } from 'core-lib/utils/tokenStorage';
import { validateAndTrimString } from 'core-lib/utils/utils';
import { emailValidation } from 'core-lib/utils/validation';
import { serializeModelDetailPage } from 'testDrive/serializers/serializeModelDetailPage';
import { CAR_IMAGE, TestDriveVersion } from '../constants';
import {
  BillUploadData,
  BookingType,
  CarDetails,
  ConfirmationDetailsType,
  ContactCars,
  ContactType,
  ListingPageType,
  ListType,
  SearchResultType,
  SearchType,
  WidgetTypeMapping,
} from '../types';
import { formatAmount } from './index';

export const getUserDetails = (item) => {
  const obj: any = {};
  const profile = getLocalProfile() || {};

  if (emailValidation(item?.email).isValid) {
    obj.email = item.email;
  }
  if (validateAndTrimString(item?.first_name).isValid) {
    obj.name = `${item.first_name}`;
  }
  if (validateAndTrimString(item?.last_name).isValid) {
    obj.name = `${obj?.name ? obj?.name : ''} ${item.last_name}`;
  }
  if (!obj?.name) {
    if (profile?.full_name) {
      obj.name = profile.full_name;
    }
  }
  return obj;
};

export const getSpecs = (data) => {
  let text = '';
  if (data?.seating_capacity) {
    if (Array.isArray(data?.seating_capacity)) {
      text += `${data.seating_capacity?.[0]} seater `;
    } else {
      text += `${data.seating_capacity} `;
    }
  }
  if (data?.max_engine_displacement) {
    if (data?.seating_capacity) {
      text += `● `;
    }
    text += `${data?.max_engine_displacement}cc engine `;
  }

  if (data?.max_mileage) {
    if (data?.max_engine_displacement || data?.seating_capacity) {
      text += '●';
    }
    text += ` ${data?.max_mileage} kmpl mileage`;
  }

  if (data?.max_full_charge_driving_range) {
    if (data?.max_engine_displacement || data?.seating_capacity) {
      text += '●';
    }
    text += ` ${data?.max_full_charge_driving_range} Km/charge`;
  }

  return text;
};

const getPrice = (item) => {
  if (item?.meta_data?.special_configuration_enabled) {
    return formatAmount(item?.meta_data?.display_price);
  } else if (item?.min_price) {
    return formatAmount(item?.min_price);
  } else if (item?.chariot_response?.model_details?.min_price) {
    return formatAmount(item?.chariot_response?.model_details?.min_price);
  } else if (item?.display_price) {
    return formatAmount(item?.display_price);
  }
  return '';
};

export const serializeCarData = (item): ListType => ({
  leadSource: item?.lead_source,
  id: item?.inventory_id || item?.id,
  year: item?.year || '',
  modelName: item?.model_name || '',
  modelId: item?.model_id,
  brandId: item?.brand_id,
  variant: item?.variant || '',
  price: item?.price || '',
  brandName: item?.brand_name || '',
  priority: item?.priority,
  displayPrice: getPrice(item),
  highEndPrice:
    item?.high_end_price ||
    item?.chariot_response?.model_details?.max_price ||
    item?.chariot_response?.variant_list?.[
      item?.chariot_response?.variant_list?.length - 1
    ]?.on_road_price ||
    0,
  lowEndPrice:
    item?.low_end_price ||
    item?.chariot_response?.model_details?.min_price || 
    item?.chariot_response?.variant_list?.[0]?.on_road_price
     ||
    0,
  metaData: {
    bannerImage: getImageKitUrl({
      product: 'ImageKit',
      imageUrl:
        item?.meta_data?.vehicle_image ||
        item?.cover ||
        item?.cover_image ||
        CAR_IMAGE,
      width: 400,
    }),
    fullImage: getImageKitUrl({
      product: 'ImageKit',
      imageUrl: item?.meta_data?.full_image,
    }),
    specifications: item?.chariot_response?.key_spec_list?.map((e) => ({
      title: e?.key || '',
      subtitle: e?.value || '',
      image: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: e?.icon || '',
      }),
      formTitle: e?.title || '',
    })),
    vehicleImages: item?.meta_data?.special_configuration_enabled
      ? item?.meta_data?.vehicle_images?.map((item) =>
          getImageKitUrl({ product: 'ImageKit', imageUrl: item })
        )
      : [],
    brandLogo: getImageKitUrl({
      product: 'ImageKit',
      imageUrl: item?.meta_data?.special_configuration_enabled
        ? item?.meta_data?.brand_logo
        : item?.brand_logo ||
          item?.chariot_response?.model_details?.brand_logo ||
          item?.meta_data?.brand_logo,
    }),
    images: [
      getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.chariot_response?.model_details?.cover,
      }),
    ],
    useMetaInfo: item?.meta_data?.special_configuration_enabled,
    fetchDealersList: item?.meta_data?.fetch_dealers_list,
  },
  dealerName: item?.dealer_name,
  isBooked: item?.is_booked,
  displayModelName:
    item?.brand_name && item?.model_name
      ? `${item?.brand_name || ''} ${item?.model_name || ''}`
      : item?.display_model_name,
  leadId: item?.lead_id,
  isBestSelling: item?.is_best_selling || false,
  rating: item?.meta_data?.rating ? item?.meta_data?.rating.toFixed(1) : '',
  make: item?.make || '',
  leadType: item?.lead_type,
  ratingCount:
    item?.meta_data?.rating_count ||
    item?.chariot_response?.model_details?.rating_count,
  userAction: {
    actionType: item?.user_action?.action_type || 'DEFAULT',
  },
  title: item?.title,
  subtitle: item?.sub_title,
  bookingDate: item?.booking_date,
  userDetails: getUserDetails(item?.user_details),
  specText: item?.chariot_response?.model_details
    ? getSpecs(item?.chariot_response?.model_details)
    : getSpecs(item),
  status: item?.status,
  showBadge: item?.show_badge,
  badgeTitle: item?.badge_title,
  bodyType:
    item?.body_type || item?.chariot_response?.model_details?.body_slug?.[0] || item?.chariot_response?.body_slug?.[0],
  variantCount: item?.chariot_response?.variant_list?.length,
  testDriveFlowAllowed: item?.is_test_drive_booking_allowed,
  societyBookedLead: item?.special_lead_generated,
  exteriorImages:
    item?.chariot_response?.media_list?.['360']?.exterior?.map(
      (item) => item?.url
    ) || [],
  interiorImages:
    item?.chariot_response?.media_list?.['360']?.interior?.map(
      (item) => item?.url
    ) || [],
  showContactStrip: item?.show_contact_component || false,
  version: item?.version || TestDriveVersion.v1,
  newCarData:
    item?.version === TestDriveVersion.v2
      ? serializeModelDetailPage(item?.chariot_response)
      : null,
});

export const serializeConfirmationDetails = (
  data
): ConfirmationDetailsType => ({
  bookings: data?.booking_details?.map(
    (item): BookingType => ({
      displayModelName: item?.display_model_name,
      displayPrice: item?.display_price,
      inventoryId: item?.inventory_id,
      leadId: item?.lead_id,
      leadType: item?.lead_type,
      image: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.cover_image || CAR_IMAGE,
      }),
      modelId: item?.model_id,
      lowEndPrice: item?.min_price,
      highEndPrice: item?.max_price,
      bodyType: item?.body_type,
    })
  ),
  widgets: data?.widget_type?.map((item) => ({
    widgetType: item?.widget_type,
    payload: item?.payload,
    title: item?.header || '',
  })),
  userDetails: getUserDetails(data?.user_details),
});

export const serializeListingData = (data): ListingPageType => {
  let arr = [];
  data?.results?.forEach((item) => {
    const obj = {
      widgetType: item?.widget_type || WidgetTypeMapping.List,
      payload: item?.payload,
      data: serializeCarData(item),
    };
    arr.push(obj);
  });
  data?.widget_positions?.forEach((item) => {
    const newArray = [
      ...arr.slice(0, item?.position),
      {
        widgetType: item?.widget_type,
        payload: item?.payload,
      },
      ...arr.slice(item?.position),
    ];
    arr = newArray;
  });

  return {
    list: arr,
    userDetails: getUserDetails(data?.user_details),
  } as ListingPageType;
};

export const serializeWidgets = (type, data) => {
  switch (type) {
    case WidgetTypeMapping.PopularSearch:
      return {
        list:
          data?.results?.map(
            (item): SearchResultType => ({
              image: getImageKitUrl({
                product: 'ImageKit',
                imageUrl: item?.meta_data?.brand_logo,
              }),
              modelId: item?.model_id,
              title: item?.display_model_name,
              id: item?.inventory_id,
            })
          ) || [],
        widgetType: data?.widget_type,
        title: data?.title,
      };
    case WidgetTypeMapping.PopularBrands:
      return {
        list:
          data?.results?.map((item) => ({
            title: item?.brand_display_name,
            image: getImageKitUrl({
              product: 'ImageKit',
              imageUrl: item?.brand_icon,
            }),
            brandId: item?.brand_id,
          })) || [],
        widgetType: data?.widget_type,
        title: data?.title,
      };
    case WidgetTypeMapping.BestSellingCars:
      return {
        list:
          data?.results?.map((item) => ({
            title: item?.display_model_name,
            image: getImageKitUrl({
              product: 'ImageKit',
              imageUrl: item?.meta_data?.vehicle_image,
            }),
            id: item?.inventory_id,
            modelId: item?.model_id,
          })) || [],
        widgetType: data?.widget_type,
        title: data?.title,
      };
  }
};

export const serializeSearch = (data): SearchType => ({
  searchResults:
    data?.search_result?.map(
      (item): SearchResultType => ({
        image: getImageKitUrl({
          product: 'ImageKit',
          imageUrl: item?.brand_logo,
        }),
        modelId: item?.model_id,
        title: item?.title || '',
        id: item?.inventory_id,
      })
    ) || [],
  widgetList:
    data?.widgets?.map((item) => serializeWidgets(item?.widget_type, item)) ||
    [],
});

export const serializeBillUploadConfirmationData = (data): BillUploadData => ({
  billData: data?.results?.map((item) => ({
    status: item?.status,
    title: item?.title || '',
    subtitle: item?.subtitle || '',
    brandId: item?.brand_id,
    modelId: item?.model_id,
    inventoryId: item?.inventory_id,
    deliveryDate: item?.delivery_date || '',
    modelName: item?.model_name,
    brandName: item?.brand_name,
    image: getImageKitUrl({
      product: 'ImageKit',
      imageUrl: item?.image,
    }),
  })),
  widgets: data?.widgets?.map((item) => ({
    widgetType: item?.widget_type,
    payload: item?.payload,
  })),
  userDetails: getUserDetails(data?.user_details),
});

export const serializeContactCars = (data): ContactCars => ({
  count: data?.count,
  list: data?.results?.map(
    (item): CarDetails => ({
      brandId: item?.brand_id,
      modelId: item?.model_id,
      brandName: item?.brand_name,
      minPrice: formatAmount(item?.min_price),
      maxPrice: item?.max_price,
      modelImage:
        item?.model_image ||
        'https://parkplus-bkt-img.parkplus.io/production/fuel-bill/public/Banner013_20230727191745795308.png',
      modelName: item?.model_name,
      brandLogo: item?.brand_logo,
      displayModelName: item?.display_model_name,
      owners: {
        count: item?.vehicle_owners?.total_count,
        list: item?.vehicle_owners?.users?.map(
          (e): ContactType => ({
            clientId: e?.client_id,
            firstName: e?.first_name || '',
            lastName: e?.last_name || '',
            phoneNumber: e?.phone_number || '',
          })
        ),
      },
    })
  ),
  syncStatus: data?.sync_status,
  isContact: false,
});

export const serializeContacts = (data): ContactCars => ({
  count: data?.count,
  list: data?.results?.map((e) => ({
    clientId: e?.client_id,
    firstName: e?.first_name || '',
    lastName: e?.last_name || '',
    phoneNumber: e?.phone_number || '',
  })),
  syncStatus: data?.sync_status,
  isContact: true,
});
